import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { IconButton } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import OvErrorMessage from '../atoms/OvErrorMessage';
import OvVideoPlacementDescriptionForm from '../molecules/OvVideoPlacementDescriptionForm';
import OvVideoPlacementDeletePanel from './OvVideoPlacementDeletePanel';
import { VideoPlacement } from '../../../common/model/dto/video/video-placement';
import { getAxiosErrorMessage } from '../../../common/utils/helpers';
import { useVideoPermissions } from '../../../hooks/use-video-permissions';
import { useVideoPlacementMutations } from '../../../hooks/use-video-placement-mutations';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';

interface OvVideoPlacementRowProps {
  placement: VideoPlacement;
  onShowVideos: (key: string) => void;
}

const OvVideoPlacementRow: FC<OvVideoPlacementRowProps> = ({
  placement,
  onShowVideos,
}) => {
  const { t } = useTranslation();
  const { canUpdate, canDelete } = useVideoPermissions();
  const { updateDescription } = useVideoPlacementMutations();
  const [isDeleting, setIsDeleting] = useState(false);

  return (
    <Row aria-label={placement.description}>
      <Main>
        <Fields>
          {canUpdate ? (
            <OvVideoPlacementDescriptionForm
              id={`placement-${placement.key}-description`}
              description={placement.description}
              isSaving={updateDescription.isPending}
              onSave={(description) =>
                updateDescription.mutate({ key: placement.key, description })
              }
            />
          ) : (
            <Description>{placement.description}</Description>
          )}
          <Key>{placement.key}</Key>
          {updateDescription.error && (
            <OvErrorMessage
              message={getAxiosErrorMessage(updateDescription.error)}
            />
          )}
        </Fields>
        {canDelete && !isDeleting && (
          <IconButton
            aria-label={t('videos.placements.delete.open', {
              description: placement.description,
            })}
            onClick={() => setIsDeleting(true)}
          >
            <DeleteOutlineIcon />
          </IconButton>
        )}
      </Main>
      {isDeleting && (
        <OvVideoPlacementDeletePanel
          placement={placement}
          onCancel={() => setIsDeleting(false)}
          onShowVideos={onShowVideos}
        />
      )}
    </Row>
  );
};

export default OvVideoPlacementRow;

const Row = styled.li`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.75rem 0;
  border-bottom: 1px solid ${Colours.OV_BORDER_COLOR_V3};

  &:last-child {
    border-bottom: none;
  }
`;

const Main = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
`;

const Fields = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
`;

const Description = styled.span`
  font-weight: bold;
`;

const Key = styled.code`
  font-family: monospace;
  font-size: ${Variables.fontSizes.SMALL};
  color: ${Colours.OV_CHARCOAL};
  overflow-wrap: anywhere;
`;
